/**
 * HTTP boundary for the Operations cockpit's scope panel (Plan J, J6).
 *
 * The shapes below mirror `builder/api/routes/admin_scopes.py`'s
 * `ScopeOpsOut` and nothing else. Two of them are load-bearing and
 * neither invents a value:
 *
 * 1. **`null` is not `0`.** `bytes_on_disk` is `null` when the scope
 *    has no file or it could not be measured — a `no_mapping` build
 *    publishes a real, tiny file, so `0` would be a claim about it.
 *    `bytes_error` tells "could not be measured" apart (a string), and
 *    then the project and tenant totals are `null`: incomplete, never a
 *    smaller number.
 *    Same for `lockHoldMs`: a `failed` build records no timings at all,
 *    so `null` there means "not measured", never "no lock was taken".
 * 2. **`status: null` means the pair has NEVER built.** Its readers get
 *    a refusal rather than stale numbers, which is a different state
 *    from every other value in the union.
 *
 * It is its own module and not an addition to `lib/builder-api.ts` for
 * the same reason `dimensions-api.ts` and `embed-api.ts` are their own:
 * that file is past 900 lines and carries an intentional null byte.
 * Everything routes through `api.get`, the single point that injects
 * the JWT and prefixes `/{tenant}/` — a hand-rolled `fetch` here is how
 * a caller earns a 404 `no-prefix` under Plan E.
 */

import { api } from "@/lib/api";

/** `ck_scope_builds_status` plus the two states that are not rows. */
export type ScopeBuildStatus =
  | "building"
  | "built"
  | "failed"
  | "no_mapping"
  | "not_applicable";

export interface ScopeOpsScope {
  scope_id: string;
  scope_name: string | null;
  scope_kind: string | null;
  /** `null` = never built. */
  status: ScopeBuildStatus | string | null;
  /** The project swap filename this scope was cut from. */
  data_version: string | null;
  /** The project swap filename live right now. Equal = current. */
  project_data_version: string | null;
  row_count: number | null;
  started_at: string | null;
  /** Written only for `built`. */
  built_at: string | null;
  error_kind: string | null;
  error_phase: string | null;
  /** `building` past the loader's staleness threshold. */
  stuck: boolean;
  bytes_on_disk: number | null;
  /** `null` = measured, or no file. A string = could not be measured. */
  bytes_error: string | null;
  lock_hold_ms: number | null;
  steps_ms: number | null;
  gap_pct: number | null;
  timings_missing: boolean;
}

export interface ScopeOpsProject {
  project_slug: string;
  project_name: string;
  scope_count: number;
  /** `null` = at least one file could not be measured (incomplete). */
  bytes_on_disk: number | null;
  lock_hold_ms_total: number | null;
  last_round_duration_ms: number | null;
  last_round_at: string | null;
  last_round_failed: number | null;
  last_round_skipped_concurrent: number | null;
  scopes: ScopeOpsScope[];
}

export interface ScopeOpsTotals {
  /** `null` = at least one file could not be measured (incomplete). */
  bytes_on_disk: number | null;
  free_bytes: number;
  stuck: number;
  failed: number;
  never_built: number;
}

/** A named hole in the instrument. Rendered WITH the numbers. */
export interface ScopeOpsGap {
  key: string;
  detail: string;
}

export interface ScopeOpsOrphan {
  scope: string;
  name: string;
  bytes_on_disk: number | null;
  updated_at: string | null;
}

export interface ScopeOpsOverview {
  tenant: string;
  generated_at: string;
  stale_after_minutes: number;
  scope_count: number;
  projects: ScopeOpsProject[];
  totals: ScopeOpsTotals;
  orphan_effective: ScopeOpsOrphan[];
  measurement_gaps: ScopeOpsGap[];
}

export const scopeOpsApi = {
  /**
   * Build health for every (project, scope) of the tenant.
   *
   * Guarded by the `operations` flag, not by `manage_users` like every
   * other route of that router — this is the feed for a screen an
   * operator without `manage_users` is expected to read.
   */
  overview: () =>
    api.get<ScopeOpsOverview>("/api/builder/admin/scopes/ops/overview"),
};
